import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faNetworkWired,
  faStickyNote,
  faCalendarDay,
  faCalendarCheck,
  faToggleOn,
  faIdCard,
  faShieldAlt,
} from '@fortawesome/free-solid-svg-icons';
import { UI_COLORS, colorHelpers } from '../../utils/colors';
import { useTranslation } from 'react-i18next';

/**
 * InfoRow
 *
 * Small presentational row used inside the IP view modal to display a
 * labeled field with an icon. `highlight` toggles a subtle background
 * accent when the row is important (e.g. the IP address itself).
 */
const InfoRow = ({ icon, label, value, highlight = false }) => {
  const { t } = useTranslation();
  const emDash = t('common.symbols.emDash');
  const displayValue = value || emDash;
  return (
    <div
      className="flex items-start justify-between rounded-xl border px-4 py-3"
      style={{
        borderColor: UI_COLORS.BORDERS.LIGHT,
        backgroundColor: highlight
          ? colorHelpers.withOpacity(UI_COLORS.BRAND.PRIMARY, 0.1)
          : UI_COLORS.BACKGROUNDS.CARD,
      }}
    >
      <div className="flex items-center gap-3">
        <div
          className="flex h-10 w-10 items-center justify-center rounded-lg"
          style={{
            backgroundColor: colorHelpers.withOpacity(UI_COLORS.NAVIGATION.HOVER, 0.3),
            color: UI_COLORS.TEXT.PRIMARY,
          }}
        >
          <FontAwesomeIcon icon={icon} className="text-base" />
        </div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: UI_COLORS.TEXT.MUTED }}>
            {label}
          </p>
          <p className="text-base font-medium" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
            {displayValue}
          </p>
        </div>
      </div>
    </div>
  );
};

/**
 * getStatusStyles(isActive)
 *
 * Returns colors used to style the status pill for active/inactive
 * addresses.
 */
const getStatusStyles = (isActive) => {
  if (isActive) {
    return {
      backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.SUCCESS, 0.1),
      color: UI_COLORS.STATUS.SUCCESS,
    };
  }
  return {
    backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.1),
    color: UI_COLORS.STATUS.ERROR,
  };
};

/**
 * formatDateTime(value)
 *
 * Safe date/time formatter that returns an em-dash for falsy values or when
 * parsing fails.
 */
const formatDateTime = (value, emDash) => {
  if (!value) return emDash;
  try {
    return new Date(value).toLocaleString();
  } catch (err) {
    return emDash;
  }
};

/**
 * IpViewModal
 *
 * Read-only modal that presents detailed information about a single IP
 * address record. Uses `InfoRow` to render labeled fields and supports a
 * loading state.
 */
const IpViewModal = ({ isOpen, onClose, ip, loading = false }) => {
  const { t } = useTranslation();
  if (!isOpen) return null;

  const strings = t('ipAddresses.view', { returnObjects: true });
  const emDash = t('common.symbols.emDash');
  const statusStrings = t('ipAddresses.table.statuses', { returnObjects: true });
  const statusLabel = ip?.is_active ? statusStrings.active : statusStrings.inactive;
  const createdLabel = formatDateTime(ip?.created_at || ip?.createdAt, emDash);
  const updatedLabel = formatDateTime(ip?.updated_at || ip?.updatedAt, emDash);
  const description = ip?.description || ip?.notes || emDash;
  const recordTitle = ip?.ip_address || ip?.address || strings.header.fallbackTitle;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: UI_COLORS.BACKGROUNDS.OVERLAY }}
    >
      <style>{`.ip-view-modal{ -ms-overflow-style: none; scrollbar-width: none; } .ip-view-modal::-webkit-scrollbar{ display: none; }`}</style>
      <div
        className="ip-view-modal w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white shadow-2xl"
        style={{ backgroundColor: UI_COLORS.BACKGROUNDS.CARD }}
      >
        <div
          className="flex items-center justify-between border-b px-6 py-5"
          style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}
        >
          <div>
            <p className="text-sm font-semibold uppercase tracking-wide" style={{ color: UI_COLORS.TEXT.MUTED }}>
              {strings.header.sectionTitle}
            </p>
            <h2 className="text-2xl font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
              {recordTitle}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-2 transition-all duration-200 hover:scale-105"
            style={{
              color: UI_COLORS.TEXT.MUTED,
              backgroundColor: colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.1),
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.2);
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.1);
            }}
          >
            <FontAwesomeIcon icon={faTimes} className="text-lg" />
          </button>
        </div>

        <div className="space-y-6 px-6 py-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <div
                className="h-10 w-10 animate-spin rounded-full border-4 border-b-transparent"
                style={{ borderColor: colorHelpers.withOpacity(UI_COLORS.BRAND.PRIMARY, 0.5) }}
              ></div>
              <p className="mt-4 text-sm font-semibold" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                {strings.loading}
              </p>
            </div>
          ) : !ip ? (
            <div className="rounded-xl border px-6 py-16 text-center" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
              <FontAwesomeIcon
                icon={faShieldAlt}
                className="text-4xl opacity-60"
                style={{ color: UI_COLORS.TEXT.MUTED }}
              />
              <h3 className="mt-4 text-lg font-semibold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                {strings.empty.title}
              </h3>
              <p className="mt-2 text-sm" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                {strings.empty.message}
              </p>
            </div>
          ) : (
            <>
              <div className="grid gap-4 md:grid-cols-2">
                <InfoRow icon={faNetworkWired} label={strings.fields.ipAddress} value={ip?.ip_address || ip?.address} highlight />
                <InfoRow icon={faStickyNote} label={strings.fields.description} value={description} />
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div
                  className="rounded-xl border px-4 py-3"
                  style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}
                >
                  <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: UI_COLORS.TEXT.MUTED }}>
                    {strings.fields.status}
                  </p>
                  <div
                    className="mt-2 inline-flex items-center gap-2 rounded-full px-3 py-1 text-sm font-semibold"
                    style={getStatusStyles(ip?.is_active)}
                  >
                    <FontAwesomeIcon icon={faToggleOn} className="text-base" />
                    <span>{statusLabel}</span>
                  </div>
                </div>

                <InfoRow icon={faIdCard} label={strings.fields.recordId} value={ip?.id || ip?.ip_id} />
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <InfoRow icon={faCalendarDay} label={strings.fields.createdAt} value={createdLabel} />
                <InfoRow icon={faCalendarCheck} label={strings.fields.updatedAt} value={updatedLabel} />
              </div>
            </>
          )}
        </div>

        <div
          className="flex items-center justify-end gap-3 border-t px-6 py-4"
          style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}
        >
          <button
            onClick={onClose}
            className="rounded-xl px-5 py-3 font-semibold transition-all duration-200 hover:scale-105"
            style={{
              backgroundColor: 'transparent',
              color: UI_COLORS.TEXT.MUTED,
              border: `1px solid ${UI_COLORS.BORDERS.MEDIUM}`,
            }}
          >
            {strings.buttons.close}
          </button>
        </div>
      </div>
    </div>
  );
};

export default IpViewModal;
